'use client';

import config from 'config';
import { ROUTES } from 'constant';
import Image from 'next/image';
import { useRouter } from 'next/navigation';

import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { clearCartPackages } from 'store/reducers/cartSlice';
import { closeGuestModal } from 'store/reducers/guestSlice';
import { toggleCartBasket } from 'store/reducers/productsSlice';
import { setRedirectFlow } from 'store/reducers/userSlice';

import {
  buildLoginUrlWithReturnPath,
  rememberCurrentPageForPostAuthLogin
} from 'utils';

import { GuestCheckoutType, LocationFlowType } from 'types';

import { Carousel } from 'ui';

const {
  shop: {
    isAllowGuestCheckout,
    guestCheckoutModal: { title, mainText, warningText, banners }
  }
} = config;

const GuestCheckoutModal = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const {
    colors: {
      palette: {
        components: { guestCheckoutModal: guestCheckoutModalColors }
      }
    }
  } = useTheme();

  const { isCartBasketOpen } = useAppSelector((state) => state.products);
  const { selectedPackages } = useAppSelector((state) => state.cart);
  const { isOpenGuestModal } = useAppSelector((state) => state.guest);

  const isPackagesSelected = selectedPackages && selectedPackages.length > 0;

  const handleCloseCartBasket = () => {
    if (isCartBasketOpen) {
      dispatch(toggleCartBasket());
    }
  };

  const handleLogin = () => {
    dispatch(closeGuestModal());
    dispatch(setRedirectFlow(LocationFlowType.BASKET));
    handleCloseCartBasket();
    const returnPath = `${window.location.pathname}${window.location.search}`;
    rememberCurrentPageForPostAuthLogin();
    router.push(buildLoginUrlWithReturnPath(ROUTES.login, returnPath));
  };

  const handleGuest = () => {
    dispatch(closeGuestModal());
    dispatch(clearCartPackages());
    handleCloseCartBasket();
    router.push(`${ROUTES.guestCheckout}?type=${GuestCheckoutType.CART}`);
  };

  const handleBack = () => dispatch(closeGuestModal());

  if (!isOpenGuestModal || !isAllowGuestCheckout) return null;

  return (
    <Modal
      isOpen={isOpenGuestModal}
      onClose={handleBack}
      size={{ base: '3xl', md: '6xl' }}
      isCentered
    >
      <ModalOverlay
        bg={guestCheckoutModalColors?.overlayBackground || 'blackAlpha.600'}
        opacity='0.1'
      />
      <ModalContent borderRadius='12px' overflow='hidden' margin='15px'>
        <ModalCloseButton
          display={{ base: 'flex', md: 'none' }}
          alignItems='center'
          justifyContent='center'
          w='40px'
          h='40px'
          zIndex='1'
          bg={guestCheckoutModalColors?.closeButtonBackground || 'white'}
          color={guestCheckoutModalColors?.closeButtonColor || 'black'}
        />
        <ModalBody p={0}>
          <Flex gap={0} flexDirection={{ base: 'column', md: 'row' }}>
            <Box
              display={{ base: 'block', md: 'none' }}
              w='100%'
              position='relative'
              height={{ base: '200px', sm: '300px', md: '400px' }}
            >
              {banners?.[0] && (
                <Image
                  src={banners[0]}
                  alt='modal banner'
                  fill
                  style={{ objectFit: 'cover' }}
                  priority
                />
              )}
            </Box>

            <VStack
              align='start'
              spacing={{ base: '24px', md: '30px', lg: '45px' }}
              flex='1'
              px={{ base: '30px', md: '40px', '2xl': '65px' }}
              py={{ base: '24px', md: '40px' }}
            >
              <Text
                fontWeight='semibold'
                fontSize='18px'
                textAlign='center'
                w='100%'
              >
                {title}
              </Text>

              <VStack
                flex={1}
                py='30px'
                w='100%'
                borderTop='1px solid'
                borderBottom='1px solid'
                borderColor={guestCheckoutModalColors?.borderColor}
                alignItems='center'
                justifyContent='center'
                gap={{ base: '30px', md: '40px' }}
              >
                {mainText && (
                  <Text
                    fontWeight='bold'
                    fontSize={{ base: '24px', md: '32px' }}
                    textAlign='center'
                    width={{ base: '100%', md: '70%' }}
                  >
                    {mainText}
                  </Text>
                )}
                {warningText && isPackagesSelected && (
                  <Text
                    fontSize='16px'
                    width={{ base: '100%', md: '70%' }}
                    textAlign='center'
                  >
                    {warningText}
                  </Text>
                )}
              </VStack>

              <VStack w='100%' justifyContent='center' mt='auto' gap='24px'>
                <Flex
                  w='100%'
                  justifyContent='center'
                  mt='auto'
                  gap='24px'
                  flexWrap='wrap'
                  flexDirection='row'
                >
                  <Button
                    w='215px'
                    variant='outline'
                    colorScheme='black'
                    height='45px'
                    fontSize='14px'
                    onClick={handleLogin}
                  >
                    Login / Register
                  </Button>
                  <Button
                    w='215px'
                    variant='solid'
                    height='45px'
                    colorScheme='black'
                    fontSize='14px'
                    onClick={handleGuest}
                  >
                    Continue as Guest
                  </Button>
                </Flex>
                <Button
                  variant='link'
                  onClick={handleBack}
                  color={guestCheckoutModalColors?.backBtnColor || 'black'}
                  textTransform='uppercase'
                  fontSize='14px'
                  mx='auto'
                >
                  Back
                </Button>
              </VStack>
            </VStack>

            <Box
              display={{ base: 'none', md: 'block' }}
              maxW='345px'
              overflow='hidden'
              width='100%'
              marginRight='-3px'
              sx={{
                '& .slick-dots': {
                  bottom: '30px',
                  '& li': {
                    button: {
                      width: '10px',
                      height: '10px',
                      border: '1px solid',
                      borderColor: guestCheckoutModalColors?.bannerBorderColor,
                      borderRadius: '50%',
                      background: 'transparent',
                      _before: {
                        display: 'none'
                      }
                    },
                    '&.slick-active': {
                      button: {
                        background:
                          guestCheckoutModalColors?.activeButtonBackground
                      }
                    }
                  }
                }
              }}
            >
              <Carousel
                banners={banners}
                settings={{
                  dots: true
                }}
              />
            </Box>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default GuestCheckoutModal;
